import {
  getSelectedNodeIDsInAllFolders,
  getSelectedNodesInAllFolders,
} from '../fileManager/selectors/selectors';

import { createSelector } from '@reduxjs/toolkit';
import { UserAccessRight } from '../../../general/model';
import { NodeTreeState, NodeDescriptor } from '@myua/nodetree';
import { getStore } from '../../models/rootSelector';
import { MoverDialogMode } from './model';

import { intersection } from 'lodash';
import memoize from 'lodash/memoize';
import {
  CHNode,
  CHNodeIsCreativeFolder,
  CHNodeIsFolder,
  CHNodeToString,
} from '../../../general/node';

export function checkHealth(contents: NodeTreeState['contents']) {
  const errors = [];
  for (const nodeID in contents) {
    const d = contents[nodeID];
    if (d.node === null) {
      errors.push(`descriptor [${nodeID}] doesn't have node`);
      continue;
    }
    // check parent
    if (d.parentNodeID !== null && !(d.parentNodeID in contents)) {
      errors.push(`parent of ${CHNodeToString(d.node)} is not in contents`);
    }
    if (d.descendants) {
      const ids = d.descendants.map((x) => x.id);
      for (const id of ids) {
        if (!(id in contents)) {
          errors.push(`parent of ${CHNodeToString(d.node)} is not in contents`);
        }
      }
    }
  }
  // console.debug('tree validation result: ', errors.length ? errors : 'tree is valid');
  return errors;
}

export function drawTree(contents: NodeTreeState['contents'], at: number): string[] {
  const d = contents[at];
  const current = CHNodeToString(d.node!);
  if (d.descendants === null) {
    if (CHNodeIsFolder(d.node!)) {
      return [`${current}  (contents not yet loaded)`];
    }
    return [current];
  }

  if (d.descendants.length === 0) {
    return [current, '  <loaded, empty>'];
  }
  const descendants = d.descendants.flatMap((descendant) => drawTree(contents, descendant.id));
  return [current, ...descendants.map((d) => '  ' + d)];
}

export const getMover = createSelector(getStore, (store) => store.mover);

export const getMoverDialog = createSelector(getMover, (mover) => mover.dialog);

export const getMoverJobs = createSelector(getMover, (m) => m.jobs);

export const getMoverDialogMode = createSelector(getMoverDialog, (dialog) => dialog.mode);
export const getInProgress = createSelector(getMoverDialog, (dialog) => dialog.inProgress);
export const getTree = createSelector(getMover, (mover) => mover.tree);
export const getTreeContents = createSelector(getTree, (tree) => tree.contents);

export const getSelectedNodeID = createSelector(getMoverDialog, (dialog) => dialog.selectedNodeID);

export const getSelectedNode = createSelector([getSelectedNodeID, getTree], (nodeID, tree) => {
  if (!nodeID) return null;
  const nd: NodeDescriptor | null = tree.contents[nodeID] || null;
  return nd ? nd.node : null;
});
export const getSourceNodeID = createSelector(getMoverDialog, (dialog) => dialog.sourceNodeID);

export const getButtonsEnabled = createSelector([getSelectedNodesInAllFolders], (selected) => {
  return {
    [MoverDialogMode.MOVE]: Boolean(
      selected.every((node) => node.perms.includes(UserAccessRight.delete)) && selected.length > 0,
    ),
    [MoverDialogMode.COPY]:
      selected.every((node) => node.perms.includes(UserAccessRight.read)) && selected.length > 0,
    [MoverDialogMode.EXTERNAL]: false,
  };
});

export const getEnabled = createSelector(
  [
    getSourceNodeID,
    getSelectedNodeID,
    getSelectedNodeIDsInAllFolders,
    getMoverDialogMode,
    getInProgress,
  ],
  (sourceNodeID, selectedNodeID, selectedNodeIDsInAllFolders, mode, inProgress) => {
    if (inProgress) return false;
    if (sourceNodeID === null) return false;
    const allowSameNode = mode === MoverDialogMode.EXTERNAL;
    return (
      selectedNodeID !== null &&
      (allowSameNode || selectedNodeID !== sourceNodeID) &&
      !selectedNodeIDsInAllFolders.includes(selectedNodeID)
    );
  },
);

// создает бредкрамбс-подобный массив, опираясь на срез дерева в `records`
// не включает в себя саму ноду, т.к. рекурсивный
export const createBreadcrumbs = (
  targetNodeID: number,
  records: Record<string, NodeDescriptor>,
): CHNode[] => {
  const current = records[targetNodeID];
  if (current === undefined) return [];
  const parentNodeID = current.parentNodeID;
  if (parentNodeID === null) return [];
  return [
    ...createBreadcrumbsMemoized(parentNodeID, records),
    records[parentNodeID].node as CHNode,
  ];
};

export const createBreadcrumbsMemoized = memoize(createBreadcrumbs);

export const createFullBreadcrumbs = (
  targetNodeID: number,
  records: Record<string, NodeDescriptor>,
): CHNode[] => {
  return [
    ...createBreadcrumbsMemoized(targetNodeID, records),
    records[targetNodeID].node as CHNode,
  ];
};

// находит ближайшую креатив-папку вверх по дереву для данной ноды
// опять же, опираясь на срез дерева
const findCreativeFolder = (
  targetNodeID: number,
  records: Record<string, NodeDescriptor>,
): CHNode | undefined => {
  const breadcrumbs = createFullBreadcrumbs(targetNodeID, records);
  return breadcrumbs.find(CHNodeIsCreativeFolder);
};

// берет id этой папки
const findCreativeFolderID = (
  targetNodeID: number,
  records: Record<string, NodeDescriptor>,
): number | undefined => {
  const cf = findCreativeFolder(targetNodeID, records);
  return cf ? cf.id : undefined;
};

export enum OriginTargetCombination {
  BETWEEN_CREATIVES = 'BETWEEN_CREATIVES', // Creative A to creative B
  EXPORT = 'EXPORT', // Creative A to folder, disabled
  IMPORT = 'IMPORT', // regular file to creative
  REGULAR = 'REGULAR', // origin and target are within same creative or both outside any creatives
}

// говорит, как относятся друг к другу `origin` и `target`.
const getOriginTargetCombination = (
  origin: number,
  target: number,
  treeContents: NodeTreeState['contents'],
) => {
  if (!treeContents[origin] || !treeContents[target]) return;
  const originNode = treeContents[origin].node;
  const targetNode = treeContents[target].node;
  if (!originNode || !targetNode) return;
  const originCreativeFolderID = findCreativeFolderID(origin, treeContents);
  const targetCreativeFolderID = findCreativeFolderID(target, treeContents);
  if (originCreativeFolderID === targetCreativeFolderID) {
    return OriginTargetCombination.REGULAR;
  }
  if (originCreativeFolderID === undefined) {
    return OriginTargetCombination.IMPORT;
  }
  if (targetCreativeFolderID === undefined) {
    return OriginTargetCombination.EXPORT;
  }

  return OriginTargetCombination.BETWEEN_CREATIVES;
};

export const getTreeNodeEnabled = (
  breadcrumbs: CHNode[],
  treeContents: NodeTreeState['contents'],
  origins: CHNode[],
) => {
  const originIDs = origins.map((node) => node.id);
  const last = breadcrumbs.at(-1);
  if (last === undefined) return false;
  //console.debug(`------------------------------------------------`);

  const breadcrumbIDs = breadcrumbs.map((node) => node.id);

  // checkHealth(treeContents);
  // console.debug(`path:     ${breadcrumbs.map(CHNodeToString).join()}`);
  // console.debug(`origins:  ${origins.map(CHNodeToString).join()}`);
  // console.debug(`tree:`, treeContents);
  // console.debug(`tree:`, drawTree(treeContents, 1).join('\n'));

  // мы не можем перемещать ноду внутрь себя
  if (intersection(breadcrumbIDs, originIDs).length !== 0) {
    // console.debug(`-> fail intersection`);
    return false;
  }
  const modes = originIDs.map((originID) =>
    getOriginTargetCombination(originID, last.id, treeContents),
  );
  // console.debug(`-> modes ${modes.map((m) => (m === undefined ? 'undefined' : m)).join(',')}`);
  // все операции должны быть или IMPORT или REGULAR
  if (
    modes.some(
      (mode) =>
        !(
          mode === OriginTargetCombination.IMPORT ||
          mode === OriginTargetCombination.EXPORT ||
          mode === OriginTargetCombination.REGULAR
        ),
    )
  ) {
    // console.debug(`-> fail modes`);
    return false;
  }
  const permsOK = last.perms.includes(UserAccessRight.write);
  if (!permsOK) {
    // console.debug(`-> fail perms`);
  }
  // console.debug(`-> OK`);
  return permsOK;
};

// высчитывает, как относится каждая нода в дереве к ноде-источнику и показывает, в какие ноды можно перемещать файлы

export const getCurrentCombination = createSelector(
  [getSelectedNodeID, getSourceNodeID, getTreeContents],
  (target, origin, treeContents) => {
    if (!origin || !target) return;
    return getOriginTargetCombination(origin, target, treeContents);
  },
);
